import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import {
  RQ_DELETE_FINAL_PRODUCT_API,
  RQ_FILTER_FINAL_PRODUCT_API,
  RQ_GET_DELETED_FINAL_PRODUCTS_API,
  RQ_LOOKUP_ALL_FINAL_PRODUCTS_API,
  RQ_LOOKUP_FINAL_PRODUCT_BY_ID_API,
  RQ_REGISTER_FINAL_PRODUCT_API,
  RQ_RESTORE_FINAL_PRODUCT_API,
  RQ_UPDATE_FINAL_PRODUCT_API,
} from './constants';
import {rqCreateRequest} from './rqHttpUtils';

export const RqGetAllFinalProducts = (token, options = {}) => {
  return useQuery({
    queryKey: ['rq_get_all_final_products'],
    queryFn: async () => {
      const response = await rqCreateRequest(
        RQ_LOOKUP_ALL_FINAL_PRODUCTS_API,
        null,
        token,
      );

      return response || [];
    },
    refetchOnWindowFocus: false,
    retry: 1,
    ...options,
  });
};

export const RqGetFinalProductById = (id, token, options = {}) => {
  return useQuery({
    queryKey: ['rq_get_final_product', id],
    queryFn: async () => {
      return rqCreateRequest(
        RQ_LOOKUP_FINAL_PRODUCT_BY_ID_API,
        null,
        token,
        {id},
      );
    },
    enabled: !!id && !!token,
    refetchOnWindowFocus: false,
    retry: 1,
    ...options,
  });
};

export const RqGetFinalProductsByFilter = (
  filters,
  token,
  options = {},
) => {
  return useQuery({
    queryKey: ['rq_get_final_products_by_filter', filters],
    queryFn: async ({queryKey, signal}) => {
      const filtersData = queryKey[1];

      if (!filtersData || Object.keys(filtersData).length === 0) {
        return [];
      }

      return rqCreateRequest(
        RQ_FILTER_FINAL_PRODUCT_API,
        filtersData,
        token,
        null,
        signal,
      );
    },
    enabled: Object.keys(filters || {}).length > 0,
    keepPreviousData: true,
    ...options,
  });
};

export const RqRegisterFinalProduct = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_register_final_product'],
    mutationFn: async ({payload, token}) => {
      if (!token) {
        throw new Error('Auth token missing');
      }

      return rqCreateRequest(
        RQ_REGISTER_FINAL_PRODUCT_API,
        payload,
        token,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['rq_get_all_final_products'],
      });
    },
    ...options,
  });
};

export const RqUpdateFinalProduct = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_update_final_product'],
    mutationFn: async ({id, payload, token}) => {
      if (!token) {
        throw new Error('Auth token missing');
      }

      return rqCreateRequest(
        RQ_UPDATE_FINAL_PRODUCT_API,
        payload,
        token,
        {id},
      );
    },
    onSuccess: (data, variables) => {
      const {id} = variables;

      queryClient.invalidateQueries({
        queryKey: ['rq_get_final_product', id],
      });

      queryClient.invalidateQueries({
        queryKey: ['rq_get_all_final_products'],
      });
    },
    ...options,
  });
};

export const RqDeleteFinalProduct = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_delete_final_product'],
    mutationFn: async ({id, token}) => {
      if (!token) {
        throw new Error('Auth token missing');
      }

      return rqCreateRequest(
        RQ_DELETE_FINAL_PRODUCT_API,
        null,
        token,
        {id},
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['rq_get_all_final_products'],
      });
      queryClient.invalidateQueries({
        queryKey: ['rq_get_deleted_final_products'],
      });
    },
    ...options,
  });
};

export const RqGetDeletedFinalProducts = (token, options = {}) => {
  return useQuery({
    queryKey: ['rq_get_deleted_final_products'],
    queryFn: async () => {
      const response = await rqCreateRequest(
        RQ_GET_DELETED_FINAL_PRODUCTS_API,
        null,
        token,
      );

      return response || [];
    },
    enabled: !!token,
    refetchOnWindowFocus: false,
    retry: 1,
    ...options,
  });
};

export const RqRestoreFinalProduct = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_restore_final_product'],
    mutationFn: async ({id, token}) => {
      if (!token) {
        throw new Error('Auth token missing');
      }

      return rqCreateRequest(
        RQ_RESTORE_FINAL_PRODUCT_API,
        null,
        token,
        {id},
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['rq_get_all_final_products'],
      });
      queryClient.invalidateQueries({
        queryKey: ['rq_get_deleted_final_products'],
      });
    },
    ...options,
  });
};
